import { useState, useEffect, useMemo, useRef } from 'react'
import { supabase } from '../supabase'
import { useToast } from '../hooks/useToast'

function ModalConfirm({ msg, onConfirm, onCancel }) {
  return (
    <div className="modal-overlay" style={{ zIndex: 100 }} onClick={onCancel}>
      <div className="modal" style={{ paddingBottom: 20 }} onClick={e => e.stopPropagation()}>
        <div className="modal-handle" />
        <p style={{ fontSize: 15, marginBottom: 20, textAlign: 'center' }}>{msg}</p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={onCancel}>Cancelar</button>
          <button className="btn btn-danger" style={{ flex: 1 }} onClick={onConfirm}>Remover</button>
        </div>
      </div>
    </div>
  )
}

export default function Cestos() {
  const [cestos, setCestos] = useState([])
  const [itens, setItens] = useState([])
  const [categorias, setCategorias] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [viewCesto, setViewCesto] = useState(null)
  const [editCesto, setEditCesto] = useState(null)
  const [nome, setNome] = useState('')
  const [selecionados, setSelecionados] = useState([])
  const [busca, setBusca] = useState('')
  const [filtrocat, setFiltrocat] = useState('')
  const [saving, setSaving] = useState(false)
  const [confirm, setConfirm] = useState(null)
  const [alertaEstoque, setAlertaEstoque] = useState(null)
  const [fotoFile, setFotoFile] = useState(null)
  const [fotoPreview, setFotoPreview] = useState(null)
  const fotoInputRef = useRef(null)
  const { msg, visible, show } = useToast()

  useEffect(() => { fetchTudo() }, [])

  async function fetchTudo() {
    setLoading(true)
    const [{ data: cs }, { data: it }, { data: cats }] = await Promise.all([
      supabase.from('cestos').select('*, cesto_itens(*, itens(*))').order('nome'),
      supabase.from('itens').select('*').order('categoria').order('nome'),
      supabase.from('categorias').select('*').order('nome'),
    ])
    setCestos(cs || [])
    setItens(it || [])
    setCategorias(cats || [])
    setLoading(false)
  }

  function abrirNovo() {
    setEditCesto(null)
    setNome('')
    setSelecionados([])
    setBusca('')
    setFiltrocat('')
    setFotoFile(null)
    setFotoPreview(null)
    setShowForm(true)
  }

  function abrirEditar(cesto) {
    setViewCesto(null)
    setEditCesto(cesto)
    setNome(cesto.nome)
    setSelecionados(
      (cesto.cesto_itens || []).filter(ci => ci.itens).map(ci => ({ item: ci.itens, qty: ci.quantidade }))
    )
    setBusca('')
    setFiltrocat('')
    setFotoFile(null)
    setFotoPreview(cesto.foto_url || null)
    setShowForm(true)
  }

  function handleFotoChange(e) {
    const file = e.target.files[0]
    if (!file) return
    setFotoFile(file)
    setFotoPreview(URL.createObjectURL(file))
  }

  async function uploadFoto(id) {
    if (!fotoFile) return null
    const ext = fotoFile.name.split('.').pop()
    const path = `${id}.${ext}`
    const { error } = await supabase.storage.from('cestos').upload(path, fotoFile, { upsert: true })
    if (error) { console.error(error); show('Erro: ' + error.message); return null }
    const { data: { publicUrl } } = supabase.storage.from('cestos').getPublicUrl(path)
    return publicUrl
  }

  function toggleItem(item) {
    const existe = selecionados.find(s => s.item.id === item.id)
    if (existe) {
      setSelecionados(selecionados.filter(s => s.item.id !== item.id))
    } else {
      setSelecionados([...selecionados, { item, qty: 1 }])
    }
  }

  function setQty(itemId, qty) {
    if (qty < 1) return
    setSelecionados(selecionados.map(s => s.item.id === itemId ? { ...s, qty } : s))
  }

  const custoTotal = useMemo(() =>
    selecionados.reduce((sum, s) => sum + s.item.custo * s.qty, 0),
    [selecionados]
  )

  async function salvar() {
    if (!nome.trim()) { show('Dê um nome ao cesto'); return }
    if (selecionados.length === 0) { show('Adicione pelo menos um item'); return }
    setSaving(true)

    if (editCesto) {
      const fotoUrl = await uploadFoto(editCesto.id)
      const updateData = { nome: nome.trim(), custo: custoTotal }
      if (fotoUrl) updateData.foto_url = fotoUrl
      await supabase.from('cestos').update(updateData).eq('id', editCesto.id)
      await supabase.from('cesto_itens').delete().eq('cesto_id', editCesto.id)
      await supabase.from('cesto_itens').insert(
        selecionados.map(s => ({ cesto_id: editCesto.id, item_id: s.item.id, quantidade: s.qty, custo_unitario: s.item.custo }))
      )
      show('✅ Cesto atualizado!')
    } else {
      const { data: cesto, error } = await supabase
        .from('cestos').insert([{ nome: nome.trim(), custo: custoTotal }]).select().single()
      if (error) { show('Erro ao salvar'); setSaving(false); return }
      const fotoUrl = await uploadFoto(cesto.id)
      if (fotoUrl) await supabase.from('cestos').update({ foto_url: fotoUrl }).eq('id', cesto.id)
      await supabase.from('cesto_itens').insert(
        selecionados.map(s => ({ cesto_id: cesto.id, item_id: s.item.id, quantidade: s.qty, custo_unitario: s.item.custo }))
      )
      show('✅ Cesto cadastrado!')
    }

    setSaving(false)
    setShowForm(false)
    setEditCesto(null)
    fetchTudo()
  }

  async function deletar(cesto) {
    const { count } = await supabase.from('arranjo_cestos').select('*', { count: 'exact', head: true }).eq('cesto_id', cesto.id)
    if (count > 0) {
      show(`"${cesto.nome}" está em uso em ${count} produto(s) — remova lá primeiro`)
      setConfirm(null)
      return
    }
    const { error } = await supabase.from('cestos').delete().eq('id', cesto.id)
    if (error) {
      show('Erro ao remover cesto')
    } else {
      show('Cesto removido')
      setViewCesto(null)
      fetchTudo()
    }
    setConfirm(null)
  }

  function itensZerados(cesto) {
    return (cesto.cesto_itens || [])
      .filter(ci => ci.itens && ci.itens.movimenta_estoque !== false && ci.itens.estoque !== null && ci.itens.estoque <= 0)
      .map(ci => ci.itens.nome)
  }

  function calcCustoVivo(cesto) {
    return (cesto.cesto_itens || []).reduce((sum, ci) =>
      sum + (ci.itens?.custo ?? ci.custo_unitario) * ci.quantidade, 0)
  }

  const cats = [...new Set(itens.map(i => i.categoria))]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 className="page-title" style={{ margin: 0 }}>Cestos</h1>
        <button className="btn btn-primary btn-sm" onClick={abrirNovo} disabled={itens.length === 0}>+ Novo</button>
      </div>

      {itens.length === 0 && !loading && (
        <div className="card" style={{ background: '#fff8e1', borderColor: '#ffe082' }}>
          <p style={{ fontSize: 14, color: '#5d4037' }}>⚠️ Cadastre itens primeiro na aba <strong>Itens</strong>.</p>
        </div>
      )}

      {loading && <div className="spinner">Carregando...</div>}

      {!loading && cestos.length === 0 && itens.length > 0 && (
        <div className="empty">
          <div className="empty-icon">🧺</div>
          <p>Nenhum cesto ainda.<br />Cadastre seus cestos padrão.</p>
          <br />
          <button className="btn btn-ghost btn-sm" onClick={abrirNovo}>Criar primeiro cesto</button>
        </div>
      )}

      {cestos.map(cesto => (
        <div key={cesto.id} className="arr-card" onClick={() => setViewCesto(cesto)} style={{ cursor: 'pointer' }}>
          <div className="arr-card-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {cesto.foto_url
                ? <img src={cesto.foto_url} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                : <span>🧺</span>
              }
              <div className="arr-card-name">{cesto.nome}</div>
            </div>
            <div className="arr-card-price">R${calcCustoVivo(cesto).toFixed(2)}</div>
          </div>
          <div className="arr-card-itens">
            {cesto.cesto_itens?.map(ci => `${ci.quantidade}x ${ci.itens?.nome}`).join(' · ')}
          </div>
          {itensZerados(cesto).length > 0 && (
            <div
              style={{ marginTop: 6, fontSize: 12, color: '#c2185b', fontWeight: 600, display: 'inline-block' }}
              onClick={e => { e.stopPropagation(); setAlertaEstoque({ nome: cesto.nome, itens: itensZerados(cesto) }) }}
            >
              ⚠️ Estoque insuficiente
            </div>
          )}
        </div>
      ))}

      {/* MODAL NOVO / EDITAR CESTO */}
      {showForm && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) { setShowForm(false); setEditCesto(null) } }}>
          <div className="modal">
            <div className="modal-handle" />
            <div className="modal-title">{editCesto ? 'Editar cesto' : 'Novo cesto'}</div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div
                onClick={() => fotoInputRef.current.click()}
                style={{
                  width: 72, height: 72, borderRadius: 12, flexShrink: 0,
                  background: 'var(--bg)', border: '1px dashed var(--border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', overflow: 'hidden',
                }}
              >
                {fotoPreview
                  ? <img src={fotoPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <span style={{ fontSize: 24 }}>📷</span>
                }
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>Foto do cesto</div>
                <button className="btn btn-ghost btn-sm" onClick={() => fotoInputRef.current.click()}>
                  {fotoPreview ? 'Trocar foto' : 'Adicionar foto'}
                </button>
              </div>
              <input ref={fotoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFotoChange} />
            </div>

            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: 'var(--pink-light)', borderRadius: 10,
              padding: '10px 14px', marginBottom: 14
            }}>
              <span style={{ fontSize: 13, color: 'var(--pink-dark)', fontWeight: 600 }}>
                {selecionados.length} {selecionados.length === 1 ? 'item' : 'itens'}
              </span>
              <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--pink-dark)' }}>
                custo R${custoTotal.toFixed(2)}
              </span>
            </div>

            <div className="field">
              <label>Nome do cesto</label>
              <input
                placeholder="Ex: Cesto médio, Cesto grande..."
                value={nome}
                onChange={e => setNome(e.target.value)}
              />
            </div>

            <div className="section-label">Selecione os itens</div>

            <div className="row" style={{ marginBottom: 12 }}>
              <div className="field" style={{ marginBottom: 0 }}>
                <input placeholder="Buscar..." value={busca} onChange={e => setBusca(e.target.value)} />
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <select value={filtrocat} onChange={e => setFiltrocat(e.target.value)}>
                  <option value="">Todas</option>
                  {categorias.map(c => <option key={c.id} value={c.nome}>{c.nome}</option>)}
                </select>
              </div>
            </div>

            {cats.filter(cat => filtrocat === '' || cat === filtrocat).map(cat => {
              const itensDaCat = itens.filter(i =>
                i.categoria === cat && i.nome.toLowerCase().includes(busca.toLowerCase())
              )
              if (itensDaCat.length === 0) return null
              return (
                <div key={cat}>
                  <div style={{ fontSize: 11, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', margin: '10px 0 4px' }}>{cat}</div>
                  {itensDaCat.map(item => {
                    const sel = selecionados.find(s => s.item.id === item.id)
                    return (
                      <div key={item.id} style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '8px 10px', borderRadius: 8, marginBottom: 2,
                        background: sel ? 'var(--pink-light)' : 'transparent',
                        transition: 'background 0.15s',
                      }}>
                        <input
                          type="checkbox" checked={!!sel} onChange={() => toggleItem(item)}
                          style={{ width: 18, height: 18, accentColor: 'var(--pink)', cursor: 'pointer', flexShrink: 0 }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: sel ? 600 : 500 }}>{item.nome}</div>
                          <div style={{ fontSize: 12, color: '#888' }}>R${parseFloat(item.custo).toFixed(2)} / {item.unidade}</div>
                        </div>
                        {sel && (
                          <div className="qty-ctrl">
                            <button className="qty-btn" onClick={() => setQty(item.id, sel.qty - 1)}>−</button>
                            <span className="qty-num">{sel.qty}</span>
                            <button className="qty-btn" onClick={() => setQty(item.id, sel.qty + 1)}>+</button>
                          </div>
                        )}
                        {sel && (
                          <div style={{ fontSize: 12, color: 'var(--pink-dark)', fontWeight: 600, minWidth: 52, textAlign: 'right' }}>
                            R${(item.custo * sel.qty).toFixed(2)}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )
            })}

            {selecionados.length > 0 && (
              <div className="cost-box" style={{ marginTop: 16 }}>
                {selecionados.map(s => (
                  <div className="cost-row" key={s.item.id}>
                    <span>{s.qty}x {s.item.nome}</span>
                    <span>R${(s.item.custo * s.qty).toFixed(2)}</span>
                  </div>
                ))}
                <div className="cost-row total">
                  <span>Custo total</span>
                  <span>R${custoTotal.toFixed(2)}</span>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setShowForm(false)}>Cancelar</button>
              <button className="btn btn-primary" style={{ flex: 2 }} onClick={salvar} disabled={saving}>
                {saving ? 'Salvando...' : editCesto ? 'Salvar alterações' : 'Salvar cesto'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL VER CESTO */}
      {viewCesto && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setViewCesto(null)}>
          <div className="modal">
            <div className="modal-handle" />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {viewCesto.foto_url && <img src={viewCesto.foto_url} alt="" style={{ width: 48, height: 48, borderRadius: 10, objectFit: 'cover' }} />}
                <div className="modal-title" style={{ margin: 0 }}>🧺 {viewCesto.nome}</div>
              </div>
              <button style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#bbb' }} onClick={() => setViewCesto(null)}>×</button>
            </div>

            <div className="section-label">Composição</div>
            {viewCesto.cesto_itens?.map(ci => (
              <div key={ci.id} className="arr-item-row">
                <span className="arr-item-name">{ci.itens?.nome}</span>
                <span style={{ fontSize: 13, color: '#888' }}>{ci.quantidade}x</span>
                <span className="arr-item-subtotal">R${((ci.itens?.custo ?? ci.custo_unitario) * ci.quantidade).toFixed(2)}</span>
              </div>
            ))}

            <div className="cost-box" style={{ marginTop: 12 }}>
              <div className="cost-row total">
                <span>Custo total</span>
                <span>R${calcCustoVivo(viewCesto).toFixed(2)}</span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="btn btn-danger" style={{ flex: 1 }}
                onClick={() => setConfirm({ msg: `Remover "${viewCesto.nome}"?`, fn: () => deletar(viewCesto) })}>
                Remover
              </button>
              <button className="btn btn-ghost" style={{ flex: 2 }} onClick={() => abrirEditar(viewCesto)}>
                ✏️ Editar cesto
              </button>
            </div>
          </div>
        </div>
      )}

      {confirm && <ModalConfirm msg={confirm.msg} onConfirm={confirm.fn} onCancel={() => setConfirm(null)} />}

      {alertaEstoque && (
        <div className="modal-overlay" onClick={() => setAlertaEstoque(null)}>
          <div className="modal" style={{ paddingBottom: 20 }} onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <div className="modal-title" style={{ color: '#c2185b' }}>⚠️ Estoque insuficiente</div>
            <p style={{ fontSize: 14, color: '#666', marginBottom: 16 }}>
              Para montar <strong>{alertaEstoque.nome}</strong> faltam:
            </p>
            {alertaEstoque.itens.map(nome => (
              <div key={nome} style={{ padding: '10px 14px', marginBottom: 6, borderRadius: 8, background: '#fce4ec', fontSize: 14, fontWeight: 600, color: '#c2185b' }}>
                {nome}
              </div>
            ))}
            <button className="btn btn-ghost" style={{ marginTop: 8, width: '100%' }} onClick={() => setAlertaEstoque(null)}>
              Fechar
            </button>
          </div>
        </div>
      )}

      <div className={`toast ${visible ? 'show' : ''}`}>{msg}</div>
    </div>
  )
}
